import { useCallback, useState } from "react";

import isNil from "lodash/isNil";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import { useSchedulePermissionsContext } from "@/context-providers/SchedulePermissionsContext";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectPatientId,
  selectProviderType,
  useBookingDispatch,
} from "@/store/reducers/booking";
import AppointmentAvatarCard from "@/ui/components/schedule/booking/AppointmentAvatarCard";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import { FlatList } from "@/ui/layout";
import type { SelectedProviderData } from "@/utils/booking.util";
import { isProviderSelected } from "@/utils/booking.util";

const AppointmentProviderStep = ({ step }: BaseBookingStepProps) => {
  const { setProviderType } = useBookingDispatch();
  const selectedBookingProviderType = useAppSelector(selectProviderType);
  const selectedPatientId = useAppSelector(selectPatientId);
  const baseTestID = BookingStepBaseTestIDMap[step];
  const { isPatient } = useAppUserSelector();
  const { trackEvent } = useBookingAnalytics();

  const [selectedProviderData, setSelectedProviderData] = useState<
    SelectedProviderData | undefined
  >(() => {
    if (isNil(selectedBookingProviderType)) {
      return undefined;
    }

    return {
      providerExternalId: selectedBookingProviderType.providerExternalId,
      providerType: selectedBookingProviderType.providerType,
    };
  });

  const { onBackwardNavigation, onForwardNavigation } = useBookingNavigation({
    currentStep: step,
  });
  const { appointmentTypesForPatient } = useSchedulePermissionsContext();
  const data = selectedPatientId
    ? appointmentTypesForPatient?.(selectedPatientId)
    : [];

  const handleBackwardNavigation = () => {
    onBackwardNavigation();
  };

  const handleForwardNavigation = () => {
    const providerType = data?.find((p) =>
      isProviderSelected({
        providerExternalId: p.providerExternalId,
        providerType: p.providerType,
        selectedProviderData,
      }),
    );

    if (isNil(providerType)) {
      return;
    }

    onForwardNavigation();
  };
  const ListHeaderComponent = useCallback(
    () => <BookingStepTitle step={step} />,
    [step],
  );
  return (
    <>
      <FlatList
        data={data}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const handleProviderSelect = () => {
            if (
              !isProviderSelected({
                providerExternalId: item.providerExternalId,
                providerType: item.providerType,
                selectedProviderData,
              })
            ) {
              trackEvent({
                event: BookingAnalyticsEvent.ProviderSelection,
                source: step,
                value: item.providerType,
              });
            }
            setSelectedProviderData({
              providerExternalId: item.providerExternalId,
              providerType: item.providerType,
            });
            setProviderType(item);
          };

          return (
            <AppointmentAvatarCard
              infoLabel={item.infoText}
              isSelected={isProviderSelected({
                providerExternalId: item.providerExternalId,
                providerType: item.providerType,
                selectedProviderData,
              })}
              onSelect={handleProviderSelect}
              subtitle={item.providerType}
              userId={item.providerExternalId}
              userName={item.providerName}
              userType="provider"
            />
          );
        }}
        testID={baseTestID}
      />
      <BookingModalFooter
        isFirstStep={isPatient}
        isForwardNavigationDisabled={isNil(selectedProviderData)}
        onBackwardPress={handleBackwardNavigation}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentProviderStep;
